import { AnimatePresence } from 'framer-motion'
import { useEffect } from 'react'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

import { PageTransition } from '@/components/common/PageTransition'
import { getLocalizedPath, isLocale, resolvePreferredLocale } from '@/content/i18n'
import { useDirection } from '@/hooks/useDirection'
import { useVisitTracking } from '@/hooks/useVisitTracking'
import { useUiStore } from '@/store/useUiStore'
import { PublicTrackingPixels } from '@/features/tracking/PublicTrackingPixels'
import { SiteFooter } from '@/features/navigation/SiteFooter'
import { WhatsAppButton } from '@/components/common/WhatsAppButton'
import { SiteHeader } from '@/features/navigation/SiteHeader'

export function LocaleLayout() {
  const params = useParams<{ locale: string }>()
  const location = useLocation()
  const setLocalePreference = useUiStore((state) => state.setLocalePreference)
  const closeMenu = useUiStore((state) => state.closeMenu)
  const setNavbarPinned = useUiStore((state) => state.setNavbarPinned)
  const isValidLocale = isLocale(params.locale)
  const locale = isValidLocale ? params.locale : resolvePreferredLocale()

  const direction = useDirection(locale)

  const isAdminRoute =
    location.pathname.includes('/dashboard') ||
    location.pathname.includes('/auth/login') ||
    location.pathname.includes('/auth/forgot-password') ||
    location.pathname.includes('/auth/reset-password')
  useVisitTracking({
    path: location.pathname + location.search,
    enabled: !isAdminRoute,
  })

  useEffect(() => {
    setLocalePreference(locale)
  }, [locale, setLocalePreference])

  useEffect(() => {
    const handleScroll = () => {
      setNavbarPinned(window.scrollY > 24)
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => window.removeEventListener('scroll', handleScroll)
  }, [setNavbarPinned])

  useEffect(() => {
    closeMenu()
  }, [closeMenu, location.pathname])

  if (!isValidLocale) {
    return <Navigate replace to={getLocalizedPath(locale)} />
  }

  return (
    <div className="min-h-screen bg-white text-slate-950">
      {!isAdminRoute && <PublicTrackingPixels />}
      <SiteHeader locale={locale} direction={direction} />
      <main className="pt-24" id="main-content">
        <AnimatePresence mode="wait">
          <PageTransition key={location.pathname}>
            <Outlet />
          </PageTransition>
        </AnimatePresence>
      </main>
      <SiteFooter locale={locale} />
      <WhatsAppButton />
    </div>
  )
}
